package music.jammers.client.shared.auth.presentation.viewmodel

import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import kotlinx.coroutines.CoroutineExceptionHandler
import music.jammers.client.shared.analytics.auth.AuthAnalytics
import music.jammers.client.shared.auth.domain.model.LoginException
import music.jammers.client.shared.auth.domain.usecase.LoginUseCase
import music.jammers.client.shared.component.BaseViewModel
import music.jammers.client.shared.signin.models.AuthResponse
import music.limasoul.client.shared.navigation.Navigation
import music.limasoul.client.shared.navigation.Router
import music.limasoul.client.shared.user.domain.usecase.GetCurrentUserUseCase

class LoginViewModel(
    private val loginUseCase: LoginUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val analytics: AuthAnalytics,
    private val router: Router,
) : BaseViewModel() {

    private val _state = mutableStateOf<LoginState>(LoginState.Content)
    val state: State<LoginState> get() = _state

    fun onGoogleClicked() {
        analytics.onGoogleClicked()
        _state.value = LoginState.GoogleLogin
    }

    fun onError(exception: Exception?) {
        if (exception == null) {
            return
        }

        analytics.onError(exception)

        if (exception is LoginException) {
            processException(exception)
        } else {
            val errorMessage = exception.message ?: "unknown"
            _state.value = LoginState.Error(errorMessage)
        }
    }

    private fun processException(exception: LoginException) {
        when (exception.code) {
            CANCEL_ERROR_1,
            CANCEL_ERROR_5,
            -> {
                _state.value = LoginState.Content
            }

            NO_FOUND_ACCOUNT -> {
                _state.value = LoginState.OnCreateAccount
            }

            else -> {
                _state.value = LoginState.Error(exception.message ?: "Unknown")
            }
        }
    }

    fun onGoogleAuthResponse(response: AuthResponse) {
        when (response) {
            is AuthResponse.Success -> {
                _state.value = LoginState.GoogleLogin
                onGoogleAuthSuccess(response.token)
            }

            is AuthResponse.Error   -> {
                onError(LoginException(response.value.code, response.value.description))
            }

            AuthResponse.Progress   -> Unit

            AuthResponse.Cancelled  -> {
                _state.value = LoginState.Content
            }
        }
    }

    fun onGoogleAuthSuccess(token: String?) {
        if (token == null) {
            _state.value = LoginState.Error("Google token is null")
            return
        }

        analytics.onGoogleAuthSuccess()

        launch(
            CoroutineExceptionHandler { _, throwable ->
                _state.value = LoginState.Error(throwable.message ?: "Unknown")
            }
        ) {
            loginUseCase(token)

            val user = getCurrentUserUseCase()
            analytics.onAuthSuccess(user)

            router.navigateTo(Navigation.Event.List)
        }
    }

    fun onDialogClosed() {
        _state.value = LoginState.Content
    }

    private companion object {

        const val CANCEL_ERROR_1 = -1L
        const val CANCEL_ERROR_5 = -5L
        const val NO_FOUND_ACCOUNT = 16L
    }
}

sealed class LoginState {

    data object Content : LoginState()

    data object GoogleLogin : LoginState()

    data object OnCreateAccount : LoginState()

    class Error(val message: String) : LoginState()
}